import { IPromocode } from "@/api/promocodes/promocodes";
import { BookingStep } from "@/constants/booking";
import { Currencies } from "@/constants/currency";
import { Weekday } from "@/constants/dates";
import {
  IAccountingInfo,
  PaymentCard,
  // PaymentTool,
} from "@/types/db/accounting";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import { PaymentMethod } from "@smeetz/orders";

export interface IBookingState {
  bookings: { [uuid: string]: IProductSlotBooking };
  bookingList: { uuid: string; reference: string }[];
  bookingsSteps: IBookingsSteps;
  // isLoading: boolean;
  selectedBookingUUID: string;
  bookingsAmountsTrack: IBookingPaymentTrack;
  bookingsDiscountsTrack: IBookingDiscountTrack;
  promocodes: IPromocodeTrack[];
  bookingsPendingSynchronization: IBookingsPendingSync;
  adyenResponseStatusTrack: IAdyenResponseStatusTrack;
  currentOrderSessionId: string | null;
  recentTabNames: string[];
  currentBookingCount: number;
  bookingsWaiversTrack: { [bookingUUID: string]: IBookingWaiverTrack };
  bookingChanges: BookingChangesTrack;
}

export enum ISynchingReason {
  Promocode = "Promocode",
  Edit_Order = "Edit_Order",
  Scanning = "Scanning",
}

export interface IBookingPaymentTrack {
  [BookingUUID: string]: IBookingPayment;
}

// like following {"booking1": {pssDiscounts: [{pssId, pssOriginalPriceValue, pssNewPriceValue}, {pssId, pssOriginalPriceValue, pssNewPriceValue}]}}

export interface IBookingDiscountTrack {
  [BookingUUID: string]: IBookingDiscount;
}

export interface IBookingDiscount {
  isApplied: boolean; // whether or not the discount has been applied and accountings updated or not
  type: DiscountType; // percentage discount or a fixed value;
  value: number; // e.g: 10 CHF, 10% ...
  isOnAllOrder?: boolean; // whether discount applied on all the tickets or on some specific tickets
  pricesIds: number[]; // tickets that will be considered with this discount
  pssDiscounts: IPSSDiscount[];
  discountTotalValue: number; // 5900 cents
}

export enum DiscountType {
  Percentage_Discount = 1,
  Fixed_Value_Discount = 2,
}

export const DiscountTypes = DiscountType;

export interface IPSSDiscount {
  pssId: string;
  priceId: number;
  pssUUID: string | null;
  pssOriginalPriceValue: number;
  pssNewPriceValue: number;
  categoryId?: number;
  applyTime: string;
}

export interface IBookingPayment {
  currentAmount: number;
  currency: Currencies;
  paymentTool: PaymentMethod;
  paymentToolId?: string | null;
  paymentCard: PaymentCard | null;
  confirmedAmounts: IConfirmedAmount[];
  // For the bookings that support splitPayment, splitPaymentAmount is the amount to be paid
  // if the main currency is CHF and payment was done in another currency, then splitPaymentAmount will be rounded
  splitPaymentAmount?: number;
  // if the main currency is CHF and payment was done in another currency, this will store the splitPaymentAmount before rounding
  // therefore preventing inaccurate results when converting back to the payment currency
  splitAmountProvided?: number;
}

export interface IConfirmedAmount {
  id: string;
  confirmedAmount: number;
  accountingInfo: IAccountingInfo;
}

export interface IBookingsSteps {
  [bookingUUID: string]: BookingStep;
}

// eslint-disable-next-line
export interface IBookingPrintPostResponse { }

export interface IPromocodeTrack {
  bookingUuid: string;
  createdAt: string;
  id: string; // "promocodeId:bookingUuid"
  promocode: IPromocode;
  token: string;
  isValidAllDays: boolean;
  validDays: Weekday[]; // array of week days on which a promocode is available
  pssUuids: string[]; // pss uuids on which the promocode is applied
  offerIds: number[];
  membershipId?: number;
}

export enum ReceiptSignatureErrors {
  INVALID_DATA = "INVALID_DATA",
  UNUSABLE_DEVICE = "UNUSABLE_DEVICE",
  NOT_AUTHENTICATED_TO_FON = "NOT_AUTHENTICATED_TO_FON",
  SCU_NOT_SET = "SCU_NOT_SET",
  UNAUTHORIZED = "UNAUTHORIZED",
  INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR",
  ORGANIZER_NOT_FOUND = "ORGANIZER_NOT_FOUND",
  INVALID_ORGANIZER_INFORMATION = "INVALID_ORGANIZER_INFORMATION",
  INVALID_FON_CREDENTIALS = "INVALID_FON_CREDENTIALS",
  DEVICE_NOT_SET = "DEVICE_NOT_SET",
  INVALID_ARGUMENTS = "INVALID_ARGUMENTS",
}
export interface IBookingStartDate {
  date: string;
  quantity: number;
}

export interface IBookingTicket {
  id: number;
  name: string;
  productName: string;
}

export interface IBookingAddon {
  name: string;
  quantity: number;
}

export interface IBookingRetail {
  id: number;
  name: string;
  quantity: number;
}

// used to store a record of bookings, each containing a record of Adyen accountings and their payment response status
export type IAdyenResponseStatusTrack = Record<
  string,
  Record<string, AdyenResponseStatus>
>;

// possible adyen payment responses
export enum AdyenResponseStatus {
  Initiated,
  Completed,
  InitialPaymentFailed,
  Aborted,
  Unresponsive,
}

export enum SyncType {
  POST,
  PUT,
}

export type IBookingsPendingSync = Record<string, SyncType[]>;

// tracks booking uuids and their corresponding refund accountings that are being listened to.
export type RefundListenerTrack = Record<string, string[]>;

/**
 * Ticket removals and quantity decreases made to a booking before its sale was
 * signed, keyed by booking uuid. Reported to the Belgian FDM in the sale event
 * so the removals are part of the fiscal record.
 */
export type BookingChangesTrack = Record<string, BookingChange[]>;

export interface BookingChange {
  categoryId: string;
  quantity: number; // negative, since these track removals
  unitPrice: number; // in currency units
  vatRate: number;
  categoryName: string;
}

export interface ISignedWaiver {
  waiverId: string; // e.g. "A1B2C3D4"
  customerName: string;
  signedAt: string; // ISO string or formatted date string
  waiverTitle: string;
  numberOfDependents: number | null;
}

export interface IBookingWaiverTrack {
  linkedWaivers: ISignedWaiver[]; // waivers linked to this booking
}
